import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type CsvRow, downloadCsvFile, parseCsv, pickCsvFile, rowsToCsv } from '../lib/csv';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Profile = {
  id: string;
  name: string;
  cityName: string;
  countyName: string | null;
  isdName: string | null;
  stateName: string;
  salesTaxRate: string;
  propertyTaxRate: string;
  hotelOccupancyTaxRate: string;
  mixedBeverageShareRate: string;
  miscTaxesFeesPerWorker: string;
  householdTaxableSpendingPerWorker: string;
  utilityFranchiseFeePerJob: string;
  baseCityCostPerWorker: string;
  baseCountyCostPerWorker: string;
  baseIsdCostPerStudent: string;
  countyPropertyTaxRate: string;
  isdPropertyTaxRate: string;
  specialDistrictName: string | null;
  specialDistrictPropertyTaxRate: string | null;
  specialDistrictCostPerWorker: string | null;
  costInflationRate: string;
  countyFips: string | null;
  msaCode: string | null;
  isDefault: boolean;
  isSample?: boolean;
};

const EMPTY_FORM = {
  name: '',
  cityName: '',
  countyName: '',
  isdName: '',
  stateName: 'TX',
  salesTaxRate: '0.020000',
  propertyTaxRate: '0.005000',
  hotelOccupancyTaxRate: '0.070000',
  mixedBeverageShareRate: '0.016018',
  miscTaxesFeesPerWorker: '150',
  householdTaxableSpendingPerWorker: '12000',
  utilityFranchiseFeePerJob: '100',
  baseCityCostPerWorker: '1200',
  baseCountyCostPerWorker: '800',
  baseIsdCostPerStudent: '6000',
  countyPropertyTaxRate: '0.004000',
  isdPropertyTaxRate: '0.013000',
  specialDistrictName: '',
  specialDistrictPropertyTaxRate: '',
  specialDistrictCostPerWorker: '',
  costInflationRate: '0.030000',
  countyFips: '',
  msaCode: '',
  areaMode: 'county',
  isDefault: false,
};

type FormState = typeof EMPTY_FORM;

interface JurisdictionSuggestion {
  cityName: string;
  countyName: string;
  countyFips: string;
  msaCode: string | null;
  msaTitle: string | null;
  msaCountyCount: number | null;
  stateName: string;
  displayName: string;
  areaMode: 'msa' | 'county';
}

function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  step,
  hint,
}: {
  label: string;
  name: string;
  value: string | boolean;
  onChange: (name: string, val: string | boolean) => void;
  type?: 'text' | 'number' | 'checkbox';
  step?: number;
  hint?: string;
}) {
  if (type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(name, e.target.checked)}
          className="rounded border-slate-300"
        />
        {label}
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </label>
    );
  }
  return (
    <div>
      <label htmlFor={name} className="block text-xs font-medium text-slate-600">
        {label}
      </label>
      <input
        id={name}
        type={type}
        step={step}
        value={String(value)}
        onChange={(e) => onChange(name, e.target.value)}
        className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
      />
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function SettingsJurisdictionRoute() {
  const getToken = useSessionTokenGetter();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const [citySearch, setCitySearch] = useState('');
  const [suggestions, setSuggestions] = useState<JurisdictionSuggestion[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function lookupJurisdiction(query: string) {
    const [city, ...rest] = query.split(',').map((s) => s.trim());
    const state = rest.join(',').trim() || 'TX';
    if (!city) {
      setSuggestions([]);
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ city, state });
      const res = await fetch(`${API}/api/v1/lookup/jurisdiction?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const json = (await res.json()) as { suggestions: JurisdictionSuggestion[] };
      setSuggestions(json.suggestions);
      if (json.suggestions.length === 0)
        setLookupError('No results found. Try "City, State" format.');
    } catch (e) {
      setLookupError(e instanceof Error ? e.message : String(e));
      setSuggestions([]);
    } finally {
      setLookupLoading(false);
    }
  }

  function handleCitySearchChange(value: string) {
    setCitySearch(value);
    setSuggestions([]);
    setLookupError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 3) {
      debounceRef.current = setTimeout(() => void lookupJurisdiction(value), 600);
    }
  }

  function applySuggestion(s: JurisdictionSuggestion) {
    setForm((f) => ({
      ...f,
      cityName: s.cityName,
      countyName: s.countyName,
      stateName: s.stateName,
      countyFips: s.countyFips,
      msaCode: s.msaCode ?? '',
      areaMode: s.areaMode,
      name: f.name || `City of ${s.cityName}`,
    }));
    setCitySearch('');
    setSuggestions([]);
  }

  const fetchProfiles = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/jurisdiction-profiles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    setProfiles(json.profiles as Profile[]);
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchProfiles();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchProfiles]);

  function setField(name: string, val: string | boolean) {
    setForm((f) => ({ ...f, [name]: val }));
  }

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError(null);
    setCitySearch('');
    setSuggestions([]);
  }

  function openEdit(p: Profile) {
    setEditingId(p.id);
    setForm({
      name: p.name,
      cityName: p.cityName,
      countyName: p.countyName ?? '',
      isdName: p.isdName ?? '',
      stateName: p.stateName,
      salesTaxRate: p.salesTaxRate,
      propertyTaxRate: p.propertyTaxRate,
      hotelOccupancyTaxRate: p.hotelOccupancyTaxRate,
      mixedBeverageShareRate: p.mixedBeverageShareRate,
      miscTaxesFeesPerWorker: p.miscTaxesFeesPerWorker,
      householdTaxableSpendingPerWorker: p.householdTaxableSpendingPerWorker,
      utilityFranchiseFeePerJob: p.utilityFranchiseFeePerJob,
      baseCityCostPerWorker: p.baseCityCostPerWorker,
      baseCountyCostPerWorker: p.baseCountyCostPerWorker,
      baseIsdCostPerStudent: p.baseIsdCostPerStudent,
      countyPropertyTaxRate: p.countyPropertyTaxRate ?? '0.004000',
      isdPropertyTaxRate: p.isdPropertyTaxRate ?? '0.013000',
      specialDistrictName: p.specialDistrictName ?? '',
      specialDistrictPropertyTaxRate: p.specialDistrictPropertyTaxRate ?? '',
      specialDistrictCostPerWorker: p.specialDistrictCostPerWorker ?? '',
      costInflationRate: p.costInflationRate,
      countyFips: p.countyFips ?? '',
      msaCode: p.msaCode ?? '',
      areaMode: (p as Profile & { areaMode?: string }).areaMode ?? 'county',
      isDefault: p.isDefault,
    });
    setShowForm(true);
    setError(null);
  }

  async function handleSave() {
    if (!form.name || !form.cityName) {
      setError('Name and city name are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const url = editingId
        ? `${API}/api/v1/jurisdiction-profiles/${editingId}`
        : `${API}/api/v1/jurisdiction-profiles`;
      const method = editingId ? 'PUT' : 'POST';
      const payload = {
        ...form,
        countyName: form.countyName.trim() || null,
        isdName: form.isdName.trim() || null,
        specialDistrictName: form.specialDistrictName.trim() || null,
        specialDistrictPropertyTaxRate: form.specialDistrictPropertyTaxRate.trim() || null,
        specialDistrictCostPerWorker: form.specialDistrictCostPerWorker.trim() || null,
        countyFips: form.countyFips.trim() || null,
        msaCode: form.msaCode.trim() || null,
      };
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error ?? `${res.status}`);
      }
      await fetchProfiles();
      setShowForm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function handleDownloadTemplate() {
    // Prefer the default profile if one exists; otherwise seed the template
    // with EMPTY_FORM so users can see the full column list and type hints.
    const source = profiles.find((p) => p.isDefault) ?? profiles[0] ?? null;
    const base: Record<string, string> = source
      ? {
          name: source.name,
          cityName: source.cityName,
          countyName: source.countyName ?? '',
          isdName: source.isdName ?? '',
          stateName: source.stateName,
          salesTaxRate: source.salesTaxRate,
          propertyTaxRate: source.propertyTaxRate,
          hotelOccupancyTaxRate: source.hotelOccupancyTaxRate,
          mixedBeverageShareRate: source.mixedBeverageShareRate,
          miscTaxesFeesPerWorker: source.miscTaxesFeesPerWorker,
          householdTaxableSpendingPerWorker: source.householdTaxableSpendingPerWorker,
          utilityFranchiseFeePerJob: source.utilityFranchiseFeePerJob,
          baseCityCostPerWorker: source.baseCityCostPerWorker,
          baseCountyCostPerWorker: source.baseCountyCostPerWorker,
          baseIsdCostPerStudent: source.baseIsdCostPerStudent,
          countyPropertyTaxRate: source.countyPropertyTaxRate,
          isdPropertyTaxRate: source.isdPropertyTaxRate,
          specialDistrictName: source.specialDistrictName ?? '',
          specialDistrictPropertyTaxRate: source.specialDistrictPropertyTaxRate ?? '',
          specialDistrictCostPerWorker: source.specialDistrictCostPerWorker ?? '',
          costInflationRate: source.costInflationRate,
          countyFips: source.countyFips ?? '',
          msaCode: source.msaCode ?? '',
          isDefault: source.isDefault ? 'true' : 'false',
        }
      : Object.fromEntries(
          Object.entries(EMPTY_FORM).map(([k, v]) => [k, typeof v === 'boolean' ? String(v) : v]),
        );
    const rows: CsvRow[] = Object.entries(base).map(([k, v]) => [k, v]);
    const filename = 'Jurisdiction_Template.csv';
    downloadCsvFile(filename, rowsToCsv(rows));
  }

  async function handleUploadCsv() {
    const text = await pickCsvFile();
    if (!text) return;
    try {
      const parsed = parseCsv(text);
      const next: FormState = { ...EMPTY_FORM };
      const bag = next as unknown as Record<string, string | boolean>;
      for (const key of Object.keys(EMPTY_FORM)) {
        const raw = parsed[key];
        if (raw === undefined) continue;
        if (key === 'isDefault') {
          bag[key] = raw === 'true' || raw === '1';
        } else {
          bag[key] = raw;
        }
      }
      setEditingId(null);
      setForm(next);
      setShowForm(true);
      setError(null);
    } catch (e) {
      setError(`Could not parse CSV: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleDelete(id: string) {
    const token = await getToken();
    await fetch(`${API}/api/v1/jurisdiction-profiles/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchProfiles();
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Jurisdiction Profiles</h2>
          <p className="mt-1 text-sm text-slate-500">
            Save city tax rates and cost-of-services assumptions. The default profile auto-populates
            wizard Step 6.
          </p>
        </div>
        {!showForm && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Download template
            </button>
            <button
              type="button"
              onClick={() => void handleUploadCsv()}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Upload CSV
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              + Add profile
            </button>
          </div>
        )}
      </div>

      {profiles.some((p) => p.isDefault) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong className="font-semibold">Requires signed authorization.</strong> Changes to
          jurisdiction values now require an active{' '}
          <code className="mx-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-xs">
            profile_change
          </code>
          authorization record. Record one at{' '}
          <a href="/settings/governance" className="font-medium underline">
            Settings → Governance
          </a>{' '}
          before saving edits.
        </div>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Create / Edit form */}
      {showForm && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-800">
            {editingId ? 'Edit profile' : 'New profile'}
          </h3>
          {/* City lookup — auto-fills geography fields */}
          <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
            <label className="block text-xs font-medium text-blue-800">
              Auto-fill from city name
            </label>
            <p className="mb-2 text-xs text-blue-600">
              Type a city and state (e.g. "Austin, TX") to look up county FIPS and MSA code.
            </p>
            <div className="relative">
              <input
                type="text"
                placeholder="e.g. Amarillo, TX"
                value={citySearch}
                onChange={(e) => handleCitySearchChange(e.target.value)}
                className="block w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              {lookupLoading && (
                <span className="absolute right-3 top-2 text-xs text-blue-400">Searching…</span>
              )}
            </div>
            {lookupError && <p className="mt-1 text-xs text-red-600">{lookupError}</p>}
            {suggestions.length > 0 && (
              <ul className="mt-2 space-y-1">
                {suggestions.map((s) => (
                  <li key={s.countyFips}>
                    <button
                      type="button"
                      onClick={() => applySuggestion(s)}
                      className="w-full rounded border border-blue-200 bg-white px-3 py-2 text-left text-sm hover:bg-blue-100"
                    >
                      <span className="font-medium">{s.displayName}</span>
                      <span className="ml-2 text-xs text-slate-400">
                        FIPS {s.countyFips}
                        {s.msaCode ? ` · MSA ${s.msaCode}` : ''}
                      </span>
                      {s.areaMode === 'msa' && s.msaCountyCount != null && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-medium text-teal-700">
                          Metro ({s.msaCountyCount} counties)
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Profile name"
              name="name"
              value={form.name}
              onChange={setField}
              hint="e.g. City of Austin"
            />
            <Field label="City" name="cityName" value={form.cityName} onChange={setField} />
            <Field
              label="County"
              name="countyName"
              value={form.countyName}
              onChange={setField}
              hint="e.g. Travis County"
            />
            <Field
              label="School district (ISD)"
              name="isdName"
              value={form.isdName}
              onChange={setField}
              hint="e.g. Austin ISD"
            />
            <Field label="State" name="stateName" value={form.stateName} onChange={setField} />

            <p className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Data Spine Geography
            </p>

            <Field
              label="County FIPS code"
              name="countyFips"
              value={form.countyFips}
              onChange={setField}
              hint="5-digit code, e.g. 48113 for Dallas County TX"
            />
            <Field
              label="MSA / CBSA code"
              name="msaCode"
              value={form.msaCode}
              onChange={setField}
              hint="Census CBSA code, e.g. 19100 for Dallas–Fort Worth"
            />

            <p className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Tax Rates (decimal)
            </p>

            <Field
              label="City sales tax rate"
              name="salesTaxRate"
              value={form.salesTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate, e.g. 0.020000"
            />
            <Field
              label="City property tax rate"
              name="propertyTaxRate"
              value={form.propertyTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate, e.g. 0.0051181"
            />
            <Field
              label="Hotel Occupancy Tax (HOT)"
              name="hotelOccupancyTaxRate"
              value={form.hotelOccupancyTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate, e.g. 0.070000"
            />
            <Field
              label="Mixed beverage effective rate"
              name="mixedBeverageShareRate"
              value={form.mixedBeverageShareRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate on receipts, e.g. 0.016018"
            />

            <p className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Overlapping District Property Taxes
            </p>

            <Field
              label="County property tax rate"
              name="countyPropertyTaxRate"
              value={form.countyPropertyTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate, e.g. 0.0040"
            />
            <Field
              label="ISD (school district) property tax rate"
              name="isdPropertyTaxRate"
              value={form.isdPropertyTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Effective rate, e.g. 0.0130"
            />
            <Field
              label="Special district name (optional)"
              name="specialDistrictName"
              value={form.specialDistrictName}
              onChange={setField}
              hint="e.g. Hill Country WCID #1"
            />
            <Field
              label="Special district property tax rate"
              name="specialDistrictPropertyTaxRate"
              value={form.specialDistrictPropertyTaxRate}
              onChange={setField}
              type="number"
              step={0.0000001}
              hint="Leave blank if none"
            />
            <Field
              label="Special district cost per worker ($/yr)"
              name="specialDistrictCostPerWorker"
              value={form.specialDistrictCostPerWorker}
              onChange={setField}
              type="number"
              step={50}
              hint="Leave blank if none"
            />

            <p className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Cost of Services ($/yr)
            </p>

            <Field
              label="Misc taxes & fees per worker"
              name="miscTaxesFeesPerWorker"
              value={form.miscTaxesFeesPerWorker}
              onChange={setField}
              type="number"
              step={10}
            />
            <Field
              label="Household taxable spending per worker"
              name="householdTaxableSpendingPerWorker"
              value={form.householdTaxableSpendingPerWorker}
              onChange={setField}
              type="number"
              step={500}
            />
            <Field
              label="Utility franchise fee per job"
              name="utilityFranchiseFeePerJob"
              value={form.utilityFranchiseFeePerJob}
              onChange={setField}
              type="number"
              step={10}
            />
            <Field
              label="City cost of services per worker"
              name="baseCityCostPerWorker"
              value={form.baseCityCostPerWorker}
              onChange={setField}
              type="number"
              step={100}
            />
            <Field
              label="County cost per worker"
              name="baseCountyCostPerWorker"
              value={form.baseCountyCostPerWorker}
              onChange={setField}
              type="number"
              step={100}
            />
            <Field
              label="ISD cost per student"
              name="baseIsdCostPerStudent"
              value={form.baseIsdCostPerStudent}
              onChange={setField}
              type="number"
              step={500}
            />
            <Field
              label="Cost inflation rate"
              name="costInflationRate"
              value={form.costInflationRate}
              onChange={setField}
              type="number"
              step={0.005}
              hint="e.g. 0.030"
            />

            <div className="col-span-2">
              <Field
                label="Set as default profile"
                name="isDefault"
                value={form.isDefault}
                onChange={setField}
                type="checkbox"
                hint="Auto-populates wizard Step 6"
              />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Profile list */}
      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && profiles.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No jurisdiction profiles yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Add one to auto-populate wizard Step 6 with your city's rates.
          </p>
        </div>
      )}

      {!loading && profiles.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Profile</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">City</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Sales Tax</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Prop. Tax</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Default</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {profiles.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {p.name}
                    {p.isSample && (
                      <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                        Sample
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {p.cityName}, {p.stateName}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {(Number(p.salesTaxRate) * 100).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {(Number(p.propertyTaxRate) * 100).toFixed(3)}%
                  </td>
                  <td className="px-4 py-3">
                    {p.isDefault && (
                      <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                        Default
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEdit(p)}
                      className="mr-3 text-xs text-slate-500 hover:text-slate-900 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
